import { useQuery } from "@tanstack/react-query"

import type { ChatSessionQueryScope } from "@/features/chat/hooks/chat-request-registry"

import { getMessagesApi } from "@/api/api"
import { queryKeys } from "@/lib/query-keys"
import { SettingsService } from "@/services/settings-service"

/**
 * Hook to fetch messages for a session.
 *
 * Pass the chat's `scope` (useChatSessionQueryScope) wherever a chat store
 * exists: a deleted chat then fetches nothing, and a revived chat reads its own
 * query identity instead of anything fetched before the deletion (#3524).
 */
export function useSessionMessages(
  sessionId: string | undefined,
  cacheBuster?: string,
  scope?: ChatSessionQueryScope
) {
  const settingsService = SettingsService.getInstance()

  // Use the centralized queryKeys helper for consistency
  const queryKey = queryKeys.sessionMessagesQuery(sessionId || "", cacheBuster, scope?.incarnation)

  return useQuery({
    queryKey,
    queryFn: () => getMessagesApi(sessionId!, settingsService.token),
    enabled: !!sessionId && !scope?.disposed,
    staleTime: 1000 * 60, // 1 minute - messages can change frequently
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus - messages are loaded on demand
  })
}
